/**
 * localStorage-backed token store with app-injected key names.
 *
 * Each app keeps its historical localStorage keys (web: `applauss_*`,
 * admin: `admin_*`) — changing them would log out every existing session,
 * so the key names are constructor input, never constants of this package.
 */

/**
 * @param {Object} keys - localStorage key names, per app
 * @param {string} keys.accessToken - Key for the JWT access token
 * @param {string} keys.refreshToken - Key for the refresh token
 * @param {string} [keys.user] - Key for the serialized user object
 * @param {string} [keys.expiry] - Key for the token-expiry timestamp (ms).
 *   Omit for apps that don't track expiry client-side.
 * @param {Object} [options]
 * @param {boolean} [options.removeCorruptUser=false] - Drop the stored user
 *   entry when it fails to parse (instead of just returning null).
 * @returns {Object} Token store
 */
export function createTokenStorage(keys, { removeCorruptUser = false } = {}) {
  if (!keys?.accessToken || !keys?.refreshToken) {
    throw new Error('createTokenStorage requires accessToken and refreshToken key names');
  }

  const getAccessToken = () => localStorage.getItem(keys.accessToken);
  const getRefreshToken = () => localStorage.getItem(keys.refreshToken);

  const getExpiry = () => {
    if (!keys.expiry) return null;
    const raw = localStorage.getItem(keys.expiry);
    return raw ? parseInt(raw) : null;
  };

  const isExpired = () => {
    const expiry = getExpiry();
    // null = no expiry tracked; a stored 0 must still count as expired
    return expiry !== null && Date.now() > expiry;
  };

  const getUser = () => {
    if (!keys.user) return null;
    const raw = localStorage.getItem(keys.user);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      console.error('⚠️ Corrupted auth user data in localStorage');
      if (removeCorruptUser) {
        localStorage.removeItem(keys.user);
      }
      return null;
    }
  };

  /**
   * Persist tokens. Only provided fields are written — a refresh response
   * without a new refresh_token keeps the stored one.
   * @param {Object} data
   * @param {string} data.accessToken
   * @param {string} [data.refreshToken]
   * @param {number} [data.expiry] - Expiry timestamp in ms
   */
  const setTokens = ({ accessToken, refreshToken, expiry }) => {
    localStorage.setItem(keys.accessToken, accessToken);
    if (refreshToken) {
      localStorage.setItem(keys.refreshToken, refreshToken);
    }
    if (expiry && keys.expiry) {
      localStorage.setItem(keys.expiry, expiry.toString());
    }
  };

  const setUser = (user) => {
    if (!keys.user || !user) return;
    localStorage.setItem(keys.user, JSON.stringify(user));
  };

  const clear = () => {
    localStorage.removeItem(keys.accessToken);
    localStorage.removeItem(keys.refreshToken);
    if (keys.expiry) localStorage.removeItem(keys.expiry);
    if (keys.user) localStorage.removeItem(keys.user);
  };

  return {
    getAccessToken,
    getRefreshToken,
    getExpiry,
    isExpired,
    getUser,
    setTokens,
    setUser,
    clear,
  };
}
